import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { AlarmClock, HeartPulse, CheckCircle2 } from 'lucide-react';
import { PipelineTimingChart } from '../components/PipelineTimingChart';
import { DealflowTrends } from '../components/DealflowTrends';
import { OutreachHealthSection } from '../components/OutreachHealthSection';

function SlaHealthSection({ health }) {
  const navigate = useNavigate();
  const healthy = !health || (health.total_breached === 0 && health.total_warning === 0);
  if (healthy) {
    return (
      <div className="border border-emerald-500/25 bg-emerald-500/5 rounded-sm p-4 flex items-center gap-2" data-testid="sla-health-widget">
        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
        <span className="text-sm font-medium text-foreground">SLA Health</span>
        <span className="text-xs text-emerald-400">All stage deadlines on track — nothing breached or expiring.</span>
      </div>
    );
  }
  const flat = Object.entries(health.stages).flatMap(([slug, b]) => [
    ...b.breached.map((e) => ({ ...e, kind: 'breached', slug })),
    ...b.warning.map((e) => ({ ...e, kind: 'warning', slug })),
  ]);
  return (
    <div className={`border rounded-sm p-3 sm:p-4 ${health.total_breached > 0 ? 'bg-red-500/5 border-red-500/30' : 'bg-amber-500/5 border-amber-500/25'}`}
      data-testid="sla-health-widget">
      <div className="flex items-center gap-2 mb-2">
        <AlarmClock className={`w-4 h-4 ${health.total_breached > 0 ? 'text-red-400' : 'text-amber-400'}`} />
        <span className="text-sm font-medium text-foreground">SLA Health</span>
        <span className="text-xs text-muted-foreground">
          {health.total_breached > 0 && <span className="text-red-400 font-bold">{health.total_breached} breached</span>}
          {health.total_breached > 0 && health.total_warning > 0 && ' · '}
          {health.total_warning > 0 && <span className="text-amber-400 font-bold">{health.total_warning} expiring soon</span>}
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {flat.map((e, i) => (
          <button key={`${e.project_id}-${i}`} onClick={() => navigate(`/projects/${e.project_id}`)}
            className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-sm border transition-colors ${e.kind === 'breached' ? 'bg-red-500/10 border-red-500/30 text-red-300 hover:bg-red-500/20' : 'bg-amber-500/10 border-amber-500/25 text-amber-300 hover:bg-amber-500/20'}`}
            data-testid={`sla-health-deal-${e.project_id}`}>
            <span className="truncate max-w-[180px]">{e.deal_name || e.deal_label}</span>
            <span className="opacity-70 font-mono">
              {e.days_left != null && (e.days_left < 0 ? `${Math.abs(Math.round(e.days_left))}d over` : `${Math.round(e.days_left * 10) / 10}d`)}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function PipelineHealthPage() {
  const [slaHealth, setSlaHealth] = useState(null);

  useEffect(() => {
    api.getSlaHealth().then(res => setSlaHealth(res.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="pipeline-health-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl text-foreground tracking-tight flex items-center gap-2.5">
          <HeartPulse className="w-7 h-7 text-blue-400" /> Pipeline Health
        </h1>
        <p className="text-muted-foreground text-sm mt-1">How fast deals move, where they get stuck, which SLA deadlines and outreach touches need attention.</p>
      </div>

      <OutreachHealthSection />

      <SlaHealthSection health={slaHealth} />

      <PipelineTimingChart />

      <DealflowTrends />
    </div>
  );
}
